import React from 'react';
import type { Anchor } from '@withjoy/joykit';
import { NavDrawerTrigger } from '@shared/components/NavDrawer';
import type { BurgerMenuDrawerRenderProps, UniversalBurgerMenuEmbedHandlers } from '@apps/admin/components/UniversalBurgerMenu/UniversalBurgerMenu.types';

export type BurgerMenuRenderProps = Readonly<{
  isOpen: boolean;
}>;

export type BurgerMenuProps = Readonly<{
  enableMobileFirst?: boolean;
  drawerAnchor?: Anchor;
  /**
   * The drawer. A render prop rather than an element so the caller can defer it — nothing here
   * needs the drawer's data, and the trigger must keep rendering while the drawer is absent.
   * @see BurgerMenuDrawer.lazy
   */
  renderDrawer: (props: BurgerMenuDrawerRenderProps) => React.ReactNode;
  /** Host hooks for iframe embeds. Only `onRegisterClose` is used here — @see UniversalBurgerMenu */
  embed?: UniversalBurgerMenuEmbedHandlers;
  /**
   * Children can be either ReactNode or a render prop function.
   * Use render prop to access menu state (e.g., isOpen for styling).
   */
  children?: React.ReactNode | ((props: BurgerMenuRenderProps) => React.ReactNode);
}>;

/**
 * The admin's name for the shared `NavDrawerTrigger`. It keeps `BurgerMenuProps` as the admin call
 * sites have it — in particular the whole `embed` object — and unpacks the one handler the shared
 * trigger takes, because `UniversalBurgerMenuEmbedHandlers` is an admin concept.
 */
export const BurgerMenu: React.FC<BurgerMenuProps> = ({ drawerAnchor, embed, renderDrawer, children }) => (
  <NavDrawerTrigger
    drawerAnchor={drawerAnchor}
    onRegisterClose={embed?.onRegisterClose}
    renderDrawer={renderDrawer}
  >
    {children}
  </NavDrawerTrigger>
);
